import { useCallback, useContext, useLayoutEffect, useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { RequestContext } from '../context';
import { increaseView } from 'services/dumpert';
import { defaultCacheMaxAge, makeLoader } from './loader';
import { formatDateItem } from '../../lib/helpers';

export const useDataLoader = (service, maxAge = defaultCacheMaxAge) => {
  const { accessToken: requestAccessToken, state = {} } = useContext(RequestContext);
  const location = useLocation();
  const accessToken = requestAccessToken || (location.state && location.state.accessToken);
  return useCallback(makeLoader('-', null, service, accessToken, state, maxAge), [
    service.name,
    accessToken,
    maxAge,
  ]);
};

export const copyTextToClipboard = (text, func) => {
  const textField = document.createElement('textarea');
  textField.innerText = text;
  document.body.appendChild(textField);
  textField.select();
  document.execCommand('copy');
  textField.remove();
  func();
};

export const useIsOverflow = (ref, callback) => {
  const [isOverflow, setIsOverflow] = useState(undefined);
  useLayoutEffect(() => {
    const { current } = ref;
    const trigger = () => {
      const hasOverflow = current.scrollHeight > current.clientHeight;
      setIsOverflow(hasOverflow);
      if (callback) {
        callback(hasOverflow);
      }
    };

    if (current) {
      if ('ResizeObserver' in window) {
        new ResizeObserver(trigger).observe(current);
      }
      trigger();
    }
  }, [callback, ref]);
  return isOverflow;
};

export const useMounted = () => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  return mounted;
};

export const useUnmounted = () => {
  const unmounted = useRef(false);
  useEffect(
    () => () => {
      unmounted.current = true;
    },
    [],
  );
  return unmounted;
};

const loadedScripts = new Set();

export const useExternalScript = (src, attributes = {}) => {
  const [state, setState] = useState(loadedScripts.has(src) ? 'ready' : 'loading');

  useEffect(() => {
    if (loadedScripts.has(src)) {
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    const node = document.getElementsByTagName('script')[0];

    script.onload = () => {
      loadedScripts.add(src);
      setTimeout(() => {
        setState('ready');
      }, 1);
    };
    script.onerror = () => {
      script.parentNode?.removeChild(script);
      setTimeout(() => {
        setState('error');
      }, 1);
    };
    if (attributes) {
      for (const [k, v] of Object.entries(attributes)) {
        script.setAttribute(k, String(v));
      }
    }

    node.parentNode.insertBefore(script, node);
    setState('loading');
  }, [src]);
  return state;
};

export const useQueryCommandIsSupported = () => {
  const [isSupported, setIsSupported] = useState(false);
  useEffect(() => {
    if (document.queryCommandSupported('copy')) {
      setIsSupported(true);
    }
  }, []);
  return isSupported;
};

export const navigatorShareIsSupported = () => {
  const [isSupported, setIsSupported] = useState(false);
  useEffect(() => {
    if (navigator.share !== undefined) {
      setIsSupported(true);
    }
  }, []);
  return isSupported;
};

export const useIncreaseItemViews = (itemId) => {
  if (itemId === null) {
    return;
  }
  useEffect(() => {
    increaseView(itemId)
      .then(() => null)
      .catch(() => {
        /* no-op */
      });
    try {
      const key = 'viewedItems';
      const viewed = (window.sessionStorage.getItem(key) || '').split(',');
      window.sessionStorage.setItem(key, [...new Set([...viewed, itemId])].slice(-100).join(','));
    } catch (e) {
      // no-op
    }
  }, [itemId]);
};

export const useInputEvent = () => {
  const [key, setKey] = useState(null);
  useEffect(() => {
    const keyDownHandler = ({ code }) => setKey(code);
    const keyUpHandler = () => setKey(null);
    global.addEventListener('keydown', keyDownHandler);
    global.addEventListener('keyup', keyUpHandler);
    return () => {
      global.removeEventListener('keydown', keyDownHandler);
      global.removeEventListener('keyup', keyUpHandler);
    };
  }, []);
  return key;
};

export const useSecretCode = (secretCode) => {
  const [count, setCount] = useState(0);
  const [success, setSuccess] = useState(false);
  const key = useInputEvent();

  useEffect(() => {
    if (key == null) {
      return;
    }
    if (key !== secretCode[count]) {
      setCount(0);
      return;
    }

    setCount((state) => state + 1);
    if (count + 1 === secretCode.length) {
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setCount(0);
      }, 1000);
    }
  }, [key]);

  return success;
};

const konamiCode = [
  'ArrowUp',
  'ArrowUp',
  'ArrowDown',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ArrowLeft',
  'ArrowRight',
  'KeyB',
  'KeyA',
];

export const useKonamiCode = () => {
  const success = useSecretCode(konamiCode);
  return success;
};

export const useDidomiConsent = () => {
  const [hasConsent, setConsent] = useState(false);
  const [consentStatusForAll, setConsentStatusForAll] = useState(null);
  const [consentString, setConsentString] = useState('');

  const updateConsent = useCallback(() => {
    setConsent(!window.Didomi.shouldConsentBeCollected());
    setConsentStatusForAll(window.Didomi.getUserConsentStatusForAll());
    setConsentString(window.Didomi.getUserStatus()?.consent_string || '');
  }, [setConsent, setConsentStatusForAll]);

  useEffect(() => {
    window.didomiOnReady = window.didomiOnReady || [];
    window.didomiEventListeners = window.didomiEventListeners || [];

    window.didomiOnReady.push(updateConsent);

    window.didomiEventListeners.push({
      event: 'notice.clickagree',
      listener: updateConsent,
    });
    window.didomiEventListeners.push({
      event: 'preferences.clickagreetoall',
      listener: updateConsent,
    });
    window.didomiEventListeners.push({
      event: 'preferences.clickdisagreetoall',
      listener: updateConsent,
    });
    window.didomiEventListeners.push({
      event: 'preferences.clicksavechoices',
      listener: updateConsent,
    });
    window.addEventListener('notice.clickagree', updateConsent);
  }, [updateConsent]);

  return { hasConsent, consentStatusForAll, consentString };
};

export const useHydrationSafeDate = (date, showTime) => {
  const [safeDate, setSafeDate] = useState('');

  useEffect(() => {
    setSafeDate(formatDateItem(date, showTime));
  }, [date]);

  return safeDate;
};

export const useInView = (externalRef = null, options = {}) => {
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        setIsInView(entry.isIntersecting);
      },
      { threshold: 0.1, ...options },
    );

    if (externalRef.current) {
      observer.observe(externalRef.current);
    }

    return () => {
      if (externalRef.current) {
        observer.unobserve(externalRef.current);
      }
    };
  }, [externalRef, options]);

  return [externalRef, isInView];
};
